'use client'

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { PlayerTrack } from './player-context'
import { partyBridge, type PlayTarget } from './party-bridge'
import { showToast } from './toast'
import { getDownload } from './downloads'
import { uploadLocalForBaffle } from './baffle-upload'

export type { PlayTarget }

/**
 * État client du "Mode Hôte / Baffle partagée".
 *   - role 'host'  : cet appareil est la baffle ; il lit la file partagée.
 *   - role 'guest' : connecté à une soirée ; peut envoyer des morceaux à la baffle.
 *   - role 'off'   : utilisation normale.
 * Persisté en localStorage pour survivre aux rechargements.
 */

export type PartyRole = 'off' | 'host' | 'guest'

export type Participant = { guestId: string; name: string; canReact: boolean; online: boolean }

export type RoomState = {
  exists: boolean
  hostName?: string
  nowPlaying?: PlayerTrack | null
  queue?: Array<{ id: number; track: PlayerTrack; addedBy?: string }>
  allowControl?: boolean
  allowVolume?: boolean
  volume?: number | null
  command?: string | null
  commandTs?: number
  reaction?: string | null
  reactionTs?: number
  reactionBy?: string | null
  participants?: Participant[]
}

type PartyCtx = {
  role: PartyRole
  code: string | null
  hostName: string
  deviceName: string
  room: RoomState | null
  setDeviceName: (n: string) => void
  becomeHost: (code: string, hostName: string) => void
  joinAsGuest: (code: string, hostName?: string) => void
  leave: () => void
  /** Envoie un morceau à la file de la baffle. true si OK. */
  addToBaffle: (track: PlayerTrack) => Promise<boolean>
  /** Invité (si autorisé) : règle le volume de la baffle. */
  setBaffleVolume: (v: number) => void
  /** Invité (si autorisé) : commande de lecture sur la baffle. */
  sendCmd: (cmd: 'skip' | 'pause' | 'play') => void
  /** Hôte : autorise/interdit le contrôle invité. */
  setPermissions: (allowControl: boolean, allowVolume: boolean) => void
  /** Invité : destination quand on tape un morceau ('baffle' par défaut). */
  playTarget: PlayTarget
  setPlayTarget: (t: PlayTarget) => void
  /** Identifiant stable de cet appareil dans la soirée. */
  guestId: string
  /** Invité (si autorisé) : envoie une réaction sonore sur la baffle. */
  sendReaction: (reaction: string) => void
  /** Hôte : autorise/interdit une personne précise à réagir. */
  setReactPermission: (guestId: string, canReact: boolean) => void
  /** Cet appareil a-t-il le droit de réagir ? */
  myCanReact: boolean
}

const Ctx = createContext<PartyCtx>({
  role: 'off', code: null, hostName: '', deviceName: '', room: null,
  setDeviceName: () => {}, becomeHost: () => {}, joinAsGuest: () => {},
  leave: () => {}, addToBaffle: async () => false,
  setBaffleVolume: () => {}, sendCmd: () => {}, setPermissions: () => {},
  playTarget: 'baffle', setPlayTarget: () => {},
  guestId: '', sendReaction: () => {}, setReactPermission: () => {}, myCanReact: false,
})

export function PartyProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<PartyRole>('off')
  const [code, setCode] = useState<string | null>(null)
  const [hostName, setHostName] = useState('')
  const [deviceName, setDeviceName] = useState('')
  const [playTarget, setPlayTargetState] = useState<PlayTarget>('baffle')
  const [guestId, setGuestId] = useState('')

  // Restauration
  useEffect(() => {
    try {
      const raw = localStorage.getItem('blc.party')
      if (raw) {
        const p = JSON.parse(raw)
        if (p.role && p.code) { setRole(p.role); setCode(p.code); setHostName(p.hostName || '') }
      }
      const dn = localStorage.getItem('blc.party.device')
      if (dn) setDeviceName(dn)
      else {
        const def = 'Invité ' + Math.floor(1000 + Math.random() * 9000)
        setDeviceName(def); localStorage.setItem('blc.party.device', def)
      }
      const pt = localStorage.getItem('blc.party.target')
      if (pt === 'phone' || pt === 'baffle') setPlayTargetState(pt)
      let gid = localStorage.getItem('blc.party.guestId')
      if (!gid) { gid = 'g' + Math.random().toString(36).slice(2, 12); localStorage.setItem('blc.party.guestId', gid) }
      setGuestId(gid)
    } catch {}
  }, [])

  const setPlayTarget = useCallback((t: PlayTarget) => {
    setPlayTargetState(t)
    try { localStorage.setItem('blc.party.target', t) } catch {}
  }, [])

  // Persistance
  useEffect(() => {
    try {
      if (role === 'off' || !code) localStorage.removeItem('blc.party')
      else localStorage.setItem('blc.party', JSON.stringify({ role, code, hostName }))
    } catch {}
  }, [role, code, hostName])

  const updateDevice = useCallback((n: string) => {
    setDeviceName(n)
    try { localStorage.setItem('blc.party.device', n) } catch {}
  }, [])

  const becomeHost = useCallback((c: string, hn: string) => {
    setRole('host'); setCode(c); setHostName(hn)
    // GARANTIT que la salle existe côté serveur. Sans ça, un hôte créé
    // localement (ex. baffle vidéo : becomeHost(randCode())) n'était JAMAIS
    // enregistré → l'invité qui tapait le code tombait sur « soirée inexistante »
    // (le fameux « code à 4 chiffres qui marche pas »). createRoom est idempotent
    // (réutilise la salle si elle existe déjà), donc rappeler ici depuis un
    // chemin qui a déjà créé la salle (HubView, BaffleConnect) est sans effet.
    try {
      fetch('/api/party/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: c, hostName: hn }),
      }).catch(() => {})
    } catch {}
  }, [])
  const joinAsGuest = useCallback((c: string, hn?: string) => {
    setRole('guest'); setCode(c); if (hn) setHostName(hn)
  }, [])
  const leave = useCallback(() => { setRole('off'); setCode(null); setHostName(''); setRoom(null) }, [])

  // Polling de l'état de la salle (file, lecture, permissions, volume, commandes)
  const [room, setRoom] = useState<RoomState | null>(null)
  useEffect(() => {
    if (role === 'off' || !code) { setRoom(null); return }
    let live = true
    const tick = async () => {
      try {
        // Présence : un invité s'annonce (guestId + prénom) pour apparaître chez
        // l'hôte (liste des participants + permission de réagir).
        let url = `/api/party/state?code=${code}`
        if (role === 'guest' && guestId) {
          url += `&guestId=${encodeURIComponent(guestId)}&name=${encodeURIComponent(deviceName || 'Invité')}`
        }
        const r = await fetch(url, { cache: 'no-store' })
        const j = await r.json()
        if (!live) return
        if (j?.exists) setRoom(j)
        else { setRoom(null) }   // salle fermée
      } catch {}
    }
    tick()
    // 1200 ms : commandes/volume/file quasi-instantanés (play/pause au 1er clic).
    const iv = setInterval(tick, 1200)
    return () => { live = false; clearInterval(iv) }
  }, [role, code, guestId, deviceName])

  const post = (action: string, extra: any = {}) => {
    if (!code) return
    fetch('/api/party/control', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, action, ...extra }),
    }).catch(() => {})
  }
  const setBaffleVolume = useCallback((v: number) => post('setvolume', { volume: v }), [code])
  const sendCmd = useCallback((cmd: 'skip' | 'pause' | 'play') => post('cmd', { cmd }), [code])
  const setPermissions = useCallback((allowControl: boolean, allowVolume: boolean) => post('settings', { allowControl, allowVolume }), [code])
  const sendReaction = useCallback((reaction: string) => post('react', { reaction, guestId, by: deviceName }), [code, guestId, deviceName])
  const setReactPermission = useCallback((gid: string, canReactVal: boolean) => post('reactperm', { guestId: gid, canReact: canReactVal }), [code])
  const myCanReact = role === 'guest' && !!room?.participants?.find(p => p.guestId === guestId)?.canReact

  const addToBaffle = useCallback(async (track: PlayerTrack) => {
    // Code de la file : la soirée si on en a une, SINON le code TÉLÉCOMMANDE
    // (blc.remote.lastcode). Ainsi un téléphone connecté en « télécommande »
    // (sans avoir rejoint la soirée) peut quand même envoyer un son local : la
    // TV crée déjà un salon sous ce code et devient hôte dès qu'un son arrive.
    let sendCode = code
    if (!sendCode) { try { sendCode = localStorage.getItem('blc.remote.lastcode') || null } catch {} }
    if (!sendCode) return false
    // STREAMING LOCAL : si le morceau existe EN LOCAL (téléchargé/importé) et n'a
    // pas déjà une URL audio publique, on uploade son fichier → audioUrl, pour
    // que la baffle (TV) lise le VRAI fichier. Sinon la TV ne recevrait qu'un
    // videoId : injouable pour un import, et re-stream YouTube serveur bloqué.
    // Centralisé ICI → couvre TOUS les chemins (bouton, menu, auto-routage, lecteur).
    let audioUrl: string | undefined = (track as any).audioUrl
    let diag = audioUrl ? 'url:pre' : '-'
    try {
      if (!audioUrl) {
        const dl = await getDownload(track.videoId)
        diag = 'dl:' + (dl ? 'found' : 'NONE') + ' blob:' + (dl?.blob ? ((dl.blob as any).size || '?') : '-')
        if (dl?.blob) {
          const up = await uploadLocalForBaffle(dl.blob, dl.fileName, dl.videoId)
          audioUrl = up || undefined
          diag += ' up:' + (up ? 'OK' : 'FAIL')
        }
      }
    } catch (e: any) { diag += ' ERR:' + (e?.name || e?.message || '?') }
    // DIAG (à retirer) : voir côté serveur pourquoi audioUrl manque.
    try { const b = new XMLHttpRequest(); b.open('POST', '/api/tvlog', true); b.send('[bafcli] code=' + sendCode + ' vid=' + track.videoId + ' ' + diag) } catch {}
    try {
      const r = await fetch('/api/party/add', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: sendCode,
          track: { videoId: track.videoId, title: track.title, artist: track.artist, thumbnail: track.thumbnail, audioUrl, isPodcast: (track as any).isPodcast, isVideo: (track as any).isVideo },
          addedBy: deviceName,
        }),
      })
      const j = await r.json().catch(() => null)
      return !!j?.ok
    } catch { return false }
  }, [code, deviceName])

  // Synchronise le pont (lu par player-context.play). On n'active l'interception
  // QUE pour un invité : l'hôte joue en local (= la baffle), sans redirection.
  useEffect(() => {
    partyBridge.inParty = role === 'guest' && !!code
    partyBridge.target = playTarget
    partyBridge.send = (track) => {
      addToBaffle(track).then(ok => {
        showToast(ok
          ? { kind: 'success', msg: `Envoyé à la baffle 🔊`, duration: 1800 }
          : { kind: 'error', msg: "Impossible d'envoyer à la baffle" })
      })
    }
  }, [role, code, playTarget, addToBaffle])

  return (
    <Ctx.Provider value={{ role, code, hostName, deviceName, room, setDeviceName: updateDevice, becomeHost, joinAsGuest, leave, addToBaffle, setBaffleVolume, sendCmd, setPermissions, playTarget, setPlayTarget, guestId, sendReaction, setReactPermission, myCanReact }}>
      {children}
    </Ctx.Provider>
  )
}

export const useParty = () => useContext(Ctx)
